import { DateTime } from "luxon";
import { BookingSlot, LeaderList, Meeting, RecurrenceRule, User } from "../store";
import { allTimeZones, TimeZone } from "./scheduleUtils";

export const SHARE_MESSAGE_STYLE_OPTIONS = [
  "recommended",
  "natural",
  "groupedByDay",
  "timeZoneComparison",
  "compactList",
  "formal",
] as const;

export type ShareMessageStyle = typeof SHARE_MESSAGE_STYLE_OPTIONS[number];
export type ResolvedShareMessageStyle = Exclude<ShareMessageStyle, "recommended">;
export type NaturalMessageTone = "question" | "statement";

export interface ScheduledPerson {
  id?: number;
  firstName: string;
  lastName?: string;
}

export interface ShareMessageSchedulingContext {
  adminIncluded: boolean;
  scheduledPeople: ScheduledPerson[];
}

export interface FormatShareTimeMessageInput {
  slots: BookingSlot[];
  recurrenceRules?: RecurrenceRule[];
  primaryTimezone?: TimeZone;
  additionalTimezones?: string[] | null;
  selectedStyle: ShareMessageStyle;
  naturalTone: NaturalMessageTone;
  schedulingContext: ShareMessageSchedulingContext;
  useTimeRanges?: boolean;
  showEndTimes?: boolean;
  use24HourTime?: boolean;
  compactFallbackLength?: number;
}

export interface FormatShareTimeMessageResult {
  message: string;
  recommendedStyle: ResolvedShareMessageStyle;
  effectiveStyle: ResolvedShareMessageStyle;
}

export interface FormatShareTimeMessageHtmlInput extends FormatShareTimeMessageInput {
  baseLink: string;
}

const DEFAULT_COMPACT_FALLBACK_LENGTH = 600;

const styleLabels: Record<ShareMessageStyle, string> = {
  recommended: "Recommended",
  natural: "Natural sentence",
  groupedByDay: "Grouped by day",
  timeZoneComparison: "Multiple time zones",
  compactList: "Compact list",
  formal: "Formal",
};

export const getShareMessageStyleLabel = (style: ShareMessageStyle): string => styleLabels[style];

export const formatRecurrenceRuleText = (
  rule: RecurrenceRule,
  timezoneName: string,
  use24HourTime?: boolean,
): string => formatRecurrenceRuleLine(rule, [timezoneName], use24HourTime);

export const formatRecurrenceRuleSummary = (
  rule: RecurrenceRule,
  timezoneName: string,
): { recurrence: string; ends: string } => {
  const parts = parseRRuleParts(rule.rrule);
  const start = DateTime.fromISO(rule.dtstart).setZone(timezoneName || rule.timezone || "local");
  const dayLabel = formatRecurrenceDayLabel(parts, start);
  const cadence = formatRecurrenceCadence(parts);
  const recurrence = shouldShowRecurrenceCadence(parts)
    ? `${dayLabel}, ${cadence}`
    : dayLabel;
  return {
    recurrence,
    ends: formatRecurrenceEndSummary(parts),
  };
};

export const deriveShareMessageSchedulingContext = (
  meeting: Meeting | null | undefined,
  user: User | null | undefined,
  leaders: LeaderList,
): ShareMessageSchedulingContext => {
  const adminLeaderId = user?.profile.user_leader;
  const leaderIds = meeting?.leaders || [];
  const adminIncluded = !!adminLeaderId && (leaderIds.length === 0 || leaderIds.includes(adminLeaderId));
  const peopleById = new Map<number, ScheduledPerson>();

  meeting?.leader_info?.forEach(leader => {
    peopleById.set(leader.id, {
      id: leader.id,
      firstName: leader.first_name,
      lastName: leader.last_name,
    });
  });

  leaders.leaders.forEach(leader => {
    if (leaderIds.includes(leader.id) && !peopleById.has(leader.id)) {
      peopleById.set(leader.id, {
        id: leader.id,
        firstName: leader.first_name,
        lastName: leader.last_name,
      });
    }
  });

  if (adminIncluded && user && adminLeaderId && !peopleById.has(adminLeaderId)) {
    peopleById.set(adminLeaderId, {
      id: adminLeaderId,
      firstName: user.first_name,
      lastName: user.last_name,
    });
  }

  const scheduledPeople = leaderIds.length > 0
    ? leaderIds.flatMap(id => peopleById.get(id) ? [peopleById.get(id) as ScheduledPerson] : [])
    : (
      user
        ? [{ id: adminLeaderId, firstName: user.first_name, lastName: user.last_name }]
        : []
    );

  return {
    adminIncluded,
    scheduledPeople,
  };
};

export const recommendShareMessageStyle = ({
  slots,
  recurrenceRules,
  primaryTimezone,
  additionalTimezones,
  useTimeRanges,
}: Pick<
  FormatShareTimeMessageInput,
  "slots" | "recurrenceRules" | "primaryTimezone" | "additionalTimezones" | "useTimeRanges"
>): ResolvedShareMessageStyle => {
  if (recurrenceRules?.length) return "compactList";

  const sortedSlots = sortSlots(slots);
  const dayCount = new Set(sortedSlots.map(slot => dayKey(slot, getTimezoneName(primaryTimezone)))).size;
  const displayedTimezoneCount = 1 + (additionalTimezones?.filter(Boolean).length || 0);

  if (displayedTimezoneCount > 1) return "timeZoneComparison";
  if (sortedSlots.length === 1 && !useTimeRanges) return "natural";
  if (useTimeRanges) return dayCount > 1 ? "groupedByDay" : "natural";
  if (sortedSlots.length >= 3 && dayCount > 1) return "groupedByDay";
  if (sortedSlots.length >= 2 && sortedSlots.length <= 3 && dayCount === 1) return "natural";
  return "groupedByDay";
};

export const formatShareTimeMessage = (input: FormatShareTimeMessageInput): FormatShareTimeMessageResult => {
  const recommendedStyle = recommendShareMessageStyle(input);
  let effectiveStyle = input.selectedStyle === "recommended" ? recommendedStyle : input.selectedStyle;
  if (
    input.recurrenceRules?.length
    && (effectiveStyle === "groupedByDay" || effectiveStyle === "natural")
  ) {
    effectiveStyle = recommendedStyle;
  }
  let message = renderStyle(effectiveStyle, input);

  if (
    input.selectedStyle === "recommended"
    && effectiveStyle !== "compactList"
    && message.length > (input.compactFallbackLength || DEFAULT_COMPACT_FALLBACK_LENGTH)
  ) {
    effectiveStyle = "compactList";
    message = renderStyle(effectiveStyle, input);
  }

  return {
    message,
    recommendedStyle,
    effectiveStyle,
  };
};

export const formatShareTimeMessageHtml = (
  input: FormatShareTimeMessageHtmlInput
): FormatShareTimeMessageResult => {
  const textResult = formatShareTimeMessage(input);

  return {
    ...textResult,
    message: renderHtmlStyle(textResult.effectiveStyle, input),
  };
};

const renderStyle = (style: ResolvedShareMessageStyle, input: FormatShareTimeMessageInput): string => {
  if (input.recurrenceRules?.length) return formatRecurrenceRules(input, style);

  switch (style) {
    case "natural":
      return formatNaturalSentence(input);
    case "groupedByDay":
      return formatGroupedByDay(input);
    case "timeZoneComparison":
      return formatTimeZoneComparison(input);
    case "compactList":
      return formatCompactList(input);
    case "formal":
      return formatFormal(input);
  }
};

const renderHtmlStyle = (style: ResolvedShareMessageStyle, input: FormatShareTimeMessageHtmlInput): string => {
  if (input.recurrenceRules?.length) return formatRecurrenceRulesHtml(input, style);

  switch (style) {
    case "natural":
      return formatNaturalSentenceHtml(input);
    case "groupedByDay":
      return formatGroupedByDayHtml(input);
    case "timeZoneComparison":
      return formatTimeZoneComparisonHtml(input);
    case "compactList":
      return formatCompactListHtml(input);
    case "formal":
      return formatFormalHtml(input);
  }
};

const formatNaturalSentence = ({
  slots,
  primaryTimezone,
  naturalTone,
  schedulingContext,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
}: FormatShareTimeMessageInput): string => {
  const sortedSlots = sortSlots(slots);
  if (sortedSlots.length === 0) return "";

  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";
  const groupedSlots = groupSlotsByDay(sortedSlots, timezoneName);
  const dayEntries = Object.entries(groupedSlots);
  const timeOptions = dayEntries.map(([date, daySlots]) => {
    const times = daySlots.map(slot => formatSlotTime(
      slot, timezoneName, showEndTimes ?? useTimeRanges, use24HourTime
    ));
    return `${date} at ${joinWithOr(times)}`;
  });
  const optionsText = joinWithOr(timeOptions);

  if (naturalTone === "question") {
    return `Would ${optionsText}${timezoneAbbr ? ` ${timezoneAbbr}` : ""} work for you?`;
  }

  return `${getAvailabilitySubject(schedulingContext)} available ${optionsText}`
    + `${timezoneAbbr ? ` ${timezoneAbbr}` : ""}.`;
};

const formatNaturalSentenceHtml = ({
  slots,
  primaryTimezone,
  naturalTone,
  schedulingContext,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
  baseLink,
}: FormatShareTimeMessageHtmlInput): string => {
  const sortedSlots = sortSlots(slots);
  if (sortedSlots.length === 0) return "";

  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";
  const groupedSlots = groupSlotsByDay(sortedSlots, timezoneName);
  const dayEntries = Object.entries(groupedSlots);
  const timeOptions = dayEntries.map(([date, daySlots]) => {
    const times = daySlots.map(slot => formatSlotTimeLink(
      slot, timezoneName, showEndTimes ?? useTimeRanges, baseLink, use24HourTime
    ));
    return `${escapeHtml(date)} at ${joinHtmlWithOr(times)}`;
  });
  const optionsText = joinHtmlWithOr(timeOptions);
  const timezoneText = timezoneAbbr ? ` ${escapeHtml(timezoneAbbr)}` : "";

  if (naturalTone === "question") {
    return `<div>Would ${optionsText}${timezoneText} work for you?</div>`;
  }

  return `<div>${escapeHtml(getAvailabilitySubject(schedulingContext))} available ${optionsText}${timezoneText}.</div>`;
};

const formatGroupedByDay = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
}: FormatShareTimeMessageInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";
  const groupedSlots = groupSlotsByDay(sortSlots(slots), timezoneName);
  const lines: string[] = [];

  if (timezoneAbbr) {
    lines.push(`All times ${timezoneAbbr}`, "");
  }

  Object.entries(groupedSlots).forEach(([date, daySlots], index) => {
    if (index > 0) lines.push("");
    lines.push(date);
    daySlots.forEach(slot => lines.push(
      `• ${formatSlotTime(slot, timezoneName, showEndTimes ?? useTimeRanges, use24HourTime)}`
    ));
  });

  return lines.join("\n");
};

const formatGroupedByDayHtml = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
  baseLink,
}: FormatShareTimeMessageHtmlInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";
  const groupedSlots = groupSlotsByDay(sortSlots(slots), timezoneName);
  const lines: string[] = [];

  if (timezoneAbbr) {
    lines.push(`<div>All times ${escapeHtml(timezoneAbbr)}</div>`, "<br />");
  }

  Object.entries(groupedSlots).forEach(([date, daySlots], index) => {
    if (index > 0) lines.push("<br />");
    lines.push(`<div>${escapeHtml(date)}</div>`);
    daySlots.forEach(slot => lines.push(
      `<div>• ${formatSlotTimeLink(
        slot, timezoneName, showEndTimes ?? useTimeRanges, baseLink, use24HourTime
      )}</div>`
    ));
  });

  return lines.join("");
};

const formatTimeZoneComparison = ({
  slots,
  primaryTimezone,
  additionalTimezones,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
}: FormatShareTimeMessageInput): string => {
  const timezoneNames = [primaryTimezone?.name, ...(additionalTimezones || [])].filter(Boolean) as string[];
  const displayTimezones = timezoneNames.length > 0 ? timezoneNames : ["local"];
  const primaryTimezoneName = displayTimezones[0];
  const groupedSlots = groupSlotsByDay(sortSlots(slots), primaryTimezoneName);
  const lines: string[] = [];

  Object.entries(groupedSlots).forEach(([date, daySlots], index) => {
    if (index > 0) lines.push("");
    lines.push(date);
    daySlots.forEach(slot => {
      lines.push(displayTimezones.map(timezone => {
        const tz = getTimezone(timezone);
        return `${formatSlotTime(slot, timezone, showEndTimes ?? useTimeRanges, use24HourTime)}`
          + `${tz?.abbreviation ? ` ${tz.abbreviation}` : ""}`;
      }).join(" / "));
    });
  });

  return lines.join("\n");
};

const formatTimeZoneComparisonHtml = ({
  slots,
  primaryTimezone,
  additionalTimezones,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
  baseLink,
}: FormatShareTimeMessageHtmlInput): string => {
  const timezoneNames = [primaryTimezone?.name, ...(additionalTimezones || [])].filter(Boolean) as string[];
  const displayTimezones = timezoneNames.length > 0 ? timezoneNames : ["local"];
  const primaryTimezoneName = displayTimezones[0];
  const groupedSlots = groupSlotsByDay(sortSlots(slots), primaryTimezoneName);
  const lines: string[] = [];

  Object.entries(groupedSlots).forEach(([date, daySlots], index) => {
    if (index > 0) lines.push("<br />");
    lines.push(`<div>${escapeHtml(date)}</div>`);
    daySlots.forEach(slot => {
      lines.push(`<div>${displayTimezones.map(timezone => {
        const tz = getTimezone(timezone);
        const timezoneText = tz?.abbreviation ? ` ${escapeHtml(tz.abbreviation)}` : "";
        return `${formatSlotTimeLink(
          slot, timezone, showEndTimes ?? useTimeRanges, baseLink, use24HourTime
        )}${timezoneText}`;
      }).join(" / ")}</div>`);
    });
  });

  return lines.join("");
};

const formatCompactList = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
}: FormatShareTimeMessageInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";

  return sortSlots(slots).map(slot => (
    `• ${DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("ccc, MMM d")}, `
    + `${formatSlotTime(
      slot, timezoneName, showEndTimes ?? useTimeRanges, use24HourTime
    )}${timezoneAbbr ? ` ${timezoneAbbr}` : ""}`
  )).join("\n");
};

const formatCompactListHtml = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
  baseLink,
}: FormatShareTimeMessageHtmlInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";

  return sortSlots(slots).map(slot => {
    const date = DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("ccc, MMM d");
    return `<div>• ${escapeHtml(date)}, `
      + `${formatSlotTimeLink(
        slot, timezoneName, showEndTimes ?? useTimeRanges, baseLink, use24HourTime
      )}`
      + `${timezoneAbbr ? ` ${escapeHtml(timezoneAbbr)}` : ""}</div>`;
  }).join("");
};

const formatFormal = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
}: FormatShareTimeMessageInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";

  return sortSlots(slots).map(slot => (
    `${DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("cccc, MMM d")} at `
    + `${formatSlotTime(
      slot, timezoneName, showEndTimes ?? useTimeRanges, use24HourTime
    )}${timezoneAbbr ? ` ${timezoneAbbr}` : ""}`
  )).join("\n");
};

const formatFormalHtml = ({
  slots,
  primaryTimezone,
  useTimeRanges,
  showEndTimes,
  use24HourTime,
  baseLink,
}: FormatShareTimeMessageHtmlInput): string => {
  const timezoneName = getTimezoneName(primaryTimezone);
  const timezoneAbbr = primaryTimezone?.abbreviation || "";

  return sortSlots(slots).map(slot => {
    const date = DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("cccc, MMM d");
    return `<div>${escapeHtml(date)} at `
      + `${formatSlotTimeLink(
        slot, timezoneName, showEndTimes ?? useTimeRanges, baseLink, use24HourTime
      )}`
      + `${timezoneAbbr ? ` ${escapeHtml(timezoneAbbr)}` : ""}</div>`;
  }).join("");
};

const formatRecurrenceRules = (
  input: FormatShareTimeMessageInput,
  style: ResolvedShareMessageStyle,
): string => {
  const lines = style === "timeZoneComparison"
    ? formatRecurrenceRuleLinesForTimezones(input)
    : formatRecurrenceRuleLines(input);
  if (lines.length === 0) return "";

  if (style === "natural") {
    const optionsText = joinWithOr(lines);
    return input.naturalTone === "question"
      ? `Would ${optionsText} work for you?`
      : `${getAvailabilitySubject(input.schedulingContext)} available ${optionsText}.`;
  }

  return lines.map(line => `• ${line}`).join("\n");
};

const formatRecurrenceRulesHtml = (
  input: FormatShareTimeMessageHtmlInput,
  style: ResolvedShareMessageStyle,
): string => {
  const lines = (
    style === "timeZoneComparison"
      ? formatRecurrenceRuleLinesForTimezones(input)
      : formatRecurrenceRuleLines(input)
  ).map(escapeHtml);
  if (lines.length === 0) return "";

  if (style === "natural") {
    const optionsText = joinHtmlWithOr(lines);
    return input.naturalTone === "question"
      ? `<div>Would ${optionsText} work for you?</div>`
      : `<div>${escapeHtml(getAvailabilitySubject(input.schedulingContext))} available ${optionsText}.</div>`;
  }

  return lines.map(line => `<div>• ${line}</div>`).join("");
};

const formatRecurrenceRuleLines = ({
  recurrenceRules,
  primaryTimezone,
  showEndTimes,
  use24HourTime,
}: Pick<
  FormatShareTimeMessageInput,
  "recurrenceRules" | "primaryTimezone" | "showEndTimes" | "use24HourTime"
>): string[] => (
  (recurrenceRules || [])
    .slice()
    .sort((ruleA, ruleB) => ruleA.dtstart.localeCompare(ruleB.dtstart))
    .map(rule => formatRecurrenceRuleLine(
      rule,
      [rule.timezone || primaryTimezone?.name || "local"],
      showEndTimes,
      use24HourTime
    ))
);

const formatRecurrenceRuleLinesForTimezones = ({
  recurrenceRules,
  primaryTimezone,
  additionalTimezones,
  showEndTimes,
  use24HourTime,
}: Pick<
  FormatShareTimeMessageInput,
  "recurrenceRules" | "primaryTimezone" | "additionalTimezones" | "showEndTimes" | "use24HourTime"
>): string[] => {
  const timezoneNames = [primaryTimezone?.name, ...(additionalTimezones || [])].filter(Boolean) as string[];
  const displayTimezones = timezoneNames.length > 0 ? timezoneNames : ["local"];

  return (recurrenceRules || [])
    .slice()
    .sort((ruleA, ruleB) => ruleA.dtstart.localeCompare(ruleB.dtstart))
    .map(rule => formatRecurrenceRuleLine(rule, displayTimezones, showEndTimes, use24HourTime));
};

const formatRecurrenceRuleLine = (
  rule: RecurrenceRule,
  displayTimezones: string[],
  showEndTimes = true,
  use24HourTime?: boolean,
): string => {
  const parts = parseRRuleParts(rule.rrule);
  const primaryDisplayTimezone = displayTimezones[0] || rule.timezone || "local";
  const start = DateTime.fromISO(rule.dtstart).setZone(primaryDisplayTimezone);
  const timeFormat = use24HourTime ? "HH:mm" : "h:mm a";
  const timeText = displayTimezones.map(timezoneName => {
    const timezone = getTimezone(timezoneName);
    const zonedStart = DateTime.fromISO(rule.dtstart).setZone(timezoneName);
    const zonedEnd = zonedStart.plus({ minutes: rule.duration_minutes });
    const timezoneText = timezone?.abbreviation ? ` ${timezone.abbreviation}` : "";
    const endText = showEndTimes ? ` - ${zonedEnd.toFormat(timeFormat)}` : "";
    return `${zonedStart.toFormat(timeFormat)}${endText}${timezoneText}`;
  }).join(" / ");

  return `${formatRecurrenceDayLabel(parts, start)} from ${timeText}, `
    + `${formatRecurrenceCadence(parts)} starting ${formatShortDate(start)}`
    + formatRecurrenceLimit(parts);
};

const parseRRuleParts = (rrule: string): Record<string, string | undefined> => (
  rrule
    .replace(/^RRULE:/i, "")
    .split(";")
    .filter(Boolean)
    .reduce<Record<string, string | undefined>>((parts, part) => {
      const [key, value] = part.split("=");
      return key ? { ...parts, [key.toUpperCase()]: value } : parts;
    }, {})
);

const formatRecurrenceCadence = (parts: Record<string, string | undefined>): string => {
  const interval = Number(parts.INTERVAL || "1");
  const frequency = parts.FREQ || "WEEKLY";
  const unit = getFrequencyUnit(frequency);
  if (interval <= 1) return unit.adverb;
  return `every ${interval} ${unit.plural}`;
};

const formatRecurrenceLimit = (parts: Record<string, string | undefined>): string => {
  const count = Number(parts.COUNT || "0");

  if (count > 0) {
    return `, for ${count} ${count === 1 ? "meeting" : "meetings"}`;
  }

  if (parts.UNTIL) {
    const until = DateTime.fromFormat(parts.UNTIL, "yyyyMMdd'T'HHmmss'Z'", { zone: "utc" });
    const parsedUntil = until.isValid
      ? until
      : DateTime.fromFormat(parts.UNTIL, "yyyyMMdd", { zone: "utc" });
    if (parsedUntil.isValid) return ` until ${formatShortDateWithoutWeekday(parsedUntil)}`;
  }

  return "";
};

const formatRecurrenceEndSummary = (parts: Record<string, string | undefined>): string => {
  const count = Number(parts.COUNT || "0");

  if (count > 0) {
    return `After ${count} ${count === 1 ? "meeting" : "meetings"}`;
  }

  if (parts.UNTIL) {
    const until = DateTime.fromFormat(parts.UNTIL, "yyyyMMdd'T'HHmmss'Z'", { zone: "utc" });
    const parsedUntil = until.isValid
      ? until
      : DateTime.fromFormat(parts.UNTIL, "yyyyMMdd", { zone: "utc" });
    if (parsedUntil.isValid) return `On ${formatShortDateWithoutWeekday(parsedUntil)}`;
  }

  return "No end date";
};

const formatRecurrenceDayLabel = (parts: Record<string, string | undefined>, start: DateTime): string => {
  if (parts.FREQ === "DAILY") return "Daily";
  if (parts.FREQ === "MONTHLY") return `Monthly on day ${start.day}`;
  if (parts.FREQ === "YEARLY") return `Annually on ${formatShortDate(start).replace(/^[^,]+, /, "")}`;
  return `${start.toFormat("cccc")}s`;
};

const shouldShowRecurrenceCadence = (parts: Record<string, string | undefined>): boolean => {
  const frequency = parts.FREQ || "WEEKLY";
  return frequency === "WEEKLY";
};

const formatShortDate = (date: DateTime): string => (
  date.toFormat("ccc, MMM d").replace(", Sep ", ", Sept ")
);

const formatShortDateWithoutWeekday = (date: DateTime): string => (
  date.toFormat("MMM d").replace("Sep ", "Sept ")
);

const getFrequencyUnit = (frequency: string): { adverb: string; plural: string } => {
  switch (frequency) {
    case "DAILY":
      return { adverb: "daily", plural: "days" };
    case "MONTHLY":
      return { adverb: "monthly", plural: "months" };
    case "YEARLY":
      return { adverb: "annually", plural: "years" };
    case "WEEKLY":
    default:
      return { adverb: "weekly", plural: "weeks" };
  }
};

const getAvailabilitySubject = ({ adminIncluded, scheduledPeople }: ShareMessageSchedulingContext): string => {
  if (adminIncluded && scheduledPeople.length !== 1) return "We're";
  if (adminIncluded) return "I'm";
  if (scheduledPeople.length === 1) return `${scheduledPeople[0].firstName} is`;
  if (scheduledPeople.length > 1) return `${joinNames(scheduledPeople.map(person => person.firstName))} are`;
  return "We're";
};

const getTimezoneName = (timezone?: TimeZone): string => timezone?.name || "local";

const getTimezone = (timezoneName: string): TimeZone | undefined => (
  allTimeZones.find(zone => zone.name === timezoneName)
);

const sortSlots = (slots: BookingSlot[]): BookingSlot[] => (
  slots.slice().sort((slotA, slotB) => slotA.start.localeCompare(slotB.start))
);

const groupSlotsByDay = (slots: BookingSlot[], timezoneName: string): Record<string, BookingSlot[]> => (
  slots.reduce<Record<string, BookingSlot[]>>((groupedSlots, slot) => {
    const date = DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("cccc, MMM d");
    return {
      ...groupedSlots,
      [date]: [...(groupedSlots[date] || []), slot],
    };
  }, {})
);

const dayKey = (slot: BookingSlot, timezoneName: string): string => (
  DateTime.fromISO(slot.start).setZone(timezoneName).toFormat("yyyy-MM-dd")
);

const formatSlotTime = (
  slot: BookingSlot,
  timezoneName: string,
  includeEnd?: boolean,
  use24HourTime?: boolean,
): string => {
  const timeFormat = use24HourTime ? "HH:mm" : "h:mm a";
  const start = DateTime.fromISO(slot.start).setZone(timezoneName).toFormat(timeFormat);
  if (!includeEnd) return start;
  const end = DateTime.fromISO(slot.end).setZone(timezoneName).toFormat(timeFormat);
  return `${start} - ${end}`;
};

const formatSlotTimeLink = (
  slot: BookingSlot,
  timezoneName: string,
  includeEnd: boolean | undefined,
  baseLink: string,
  use24HourTime?: boolean,
): string => (
  `<a href="${escapeHtmlAttribute(getSlotLink(slot, baseLink))}" target="_blank">`
  + `${escapeHtml(formatSlotTime(slot, timezoneName, includeEnd, use24HourTime))}</a>`
);

const getSlotLink = (slot: BookingSlot, baseLink: string): string => (
  `${baseLink}?start=${encodeURIComponent(slot.start)}&end=${encodeURIComponent(slot.end)}`
);

const joinWithOr = (items: string[]): string => joinText(items, "or");

const joinNames = (items: string[]): string => joinText(items, "and");

const joinHtmlWithOr = (items: string[]): string => joinText(items, "or");

const joinText = (items: string[], conjunction: "and" | "or"): string => {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${conjunction} ${items[items.length - 1]}`;
};

const escapeHtml = (value: string): string => (
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")
);

const escapeHtmlAttribute = escapeHtml;
